import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Loader2,
  Scale,
  Download,
  FileText,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
  Gavel,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import {
  getLabourAnalysis,
  generateCcmaDraft,
  getCcmaDraft,
  runLabourAnalysis,
  exportLabourAnalysisPdf,
} from "@/lib/api/labour-analyzer.functions";
import { downloadBase64File } from "@/lib/download-base64";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/labour-analyzer/$id")({
  head: () => ({
    meta: [
      { title: "Matter analysis | AI Labour Law Analyzer" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AnalysisDetail,
});

const DRAFT_KINDS = [
  { v: "ccma_lra_7_11", l: "CCMA LRA Form 7.11 — Referral" },
  { v: "ccma_motivation", l: "CCMA Referral Motivation" },
  { v: "ccma_supporting_statement", l: "CCMA Supporting Statement" },
  { v: "representations", l: "Written Representations" },
  { v: "objection", l: "Objection / Points in Limine" },
  { v: "mitigation_statement", l: "Mitigation Statement" },
  { v: "appeal_letter", l: "Internal Appeal Letter" },
  { v: "grievance_letter", l: "Formal Grievance" },
  { v: "witness_statement", l: "Witness Statement" },
  { v: "settlement_proposal", l: "Settlement Proposal" },
  { v: "demand_letter", l: "Letter of Demand" },
];

function Severity({ sev }: { sev?: string }) {
  const map: Record<string, string> = {
    critical: "bg-rose-100 text-rose-900",
    high: "bg-orange-100 text-orange-900",
    medium: "bg-amber-100 text-amber-900",
    low: "bg-sky-100 text-sky-900",
  };
  const cls = map[sev ?? "low"] ?? "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${cls}`}>
      {sev ?? "low"}
    </span>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-5 shadow-soft">
      <h3 className="mb-3 text-sm font-semibold text-navy">{title}</h3>
      {children}
    </section>
  );
}

function AnalysisDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const fetchOne = useServerFn(getLabourAnalysis);
  const genDraft = useServerFn(generateCcmaDraft);
  const getDraft = useServerFn(getCcmaDraft);
  const runAi = useServerFn(runLabourAnalysis);

  const q = useQuery({
    queryKey: ["labour-analysis", id],
    queryFn: () => fetchOne({ data: { id } }),
    refetchInterval: (data: any) => (data?.state?.data?.row?.status === "analyzing" ? 4000 : false),
  });

  const [draftKind, setDraftKind] = useState<string>("ccma_lra_7_11");
  const [extra, setExtra] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [openDraftId, setOpenDraftId] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState<string>("");
  const [retrying, setRetrying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportPdf = useServerFn(exportLabourAnalysisPdf);

  async function onExportPdf() {
    setExporting(true);
    try {
      const { base64, filename } = await exportPdf({ data: { id } });
      downloadBase64File(base64, filename);
      toast.success("PDF downloaded");
    } catch (e: any) {
      toast.error(e?.message ?? "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const row = q.data?.row;
  const drafts = q.data?.drafts ?? [];
  const downloadUrl = q.data?.downloadUrl;
  const report = (row?.ai_report ?? {}) as any;

  async function openDraft(draftId: string) {
    setOpenDraftId(draftId);
    setDraftBody("Loading…");
    try {
      const { draft } = await getDraft({ data: { id: draftId } });
      setDraftBody(draft.content_md);
    } catch (e: any) {
      setDraftBody(`Error: ${e?.message ?? "Could not load draft"}`);
    }
  }

  async function onGenerate() {
    setDrafting(true);
    try {
      const { draft } = await genDraft({
        data: {
          analysisId: id,
          kind: draftKind as any,
          extraInstructions: extra.trim() || null,
        },
      });
      toast.success(`Generated: ${draft.title}`);
      qc.invalidateQueries({ queryKey: ["labour-analysis", id] });
      setExtra("");
      openDraft(draft.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not generate draft");
    } finally {
      setDrafting(false);
    }
  }

  async function onRetry() {
    setRetrying(true);
    try {
      await runAi({ data: { id } });
      toast.success("Re-analysed");
      qc.invalidateQueries({ queryKey: ["labour-analysis", id] });
    } catch (e: any) {
      toast.error(e?.message ?? "Retry failed");
    } finally {
      setRetrying(false);
    }
  }

  if (q.isLoading || !row) {
    return (
      <p className="text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading matter…
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={"/portal/labour-analyzer" as any}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" /> All matters
        </Link>
        <div className="flex flex-wrap gap-2">
          {downloadUrl && (
            <a
              href={downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy"
            >
              <Download className="h-3.5 w-3.5" /> Original file
            </a>
          )}
          {row?.ai_report && (row.status === "approved" || row.status === "admin_review") && (
            <button
              type="button"
              onClick={onExportPdf}
              disabled={exporting || row.status !== "approved"}
              title={row.status !== "approved" ? "Available after admin approval" : "Download branded PDF"}
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy disabled:cursor-not-allowed disabled:opacity-60"
            >
              {exporting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Export report as PDF
            </button>
          )}
          {row?.ai_report && (
            <button
              type="button"
              onClick={async () => {
                setDraftKind("appeal_letter");
                setDrafting(true);
                try {
                  const { draft } = await genDraft({
                    data: {
                      analysisId: id,
                      kind: "appeal_letter" as any,
                      extraInstructions:
                        "Draft a formal internal dismissal appeal letter directed to the Chairperson / Appeal Authority. Open with the employee's name, employee number, position and dismissal date. Identify each procedural and substantive ground of appeal drawn from the analysed document, citing Schedule 8 of the LRA, Sidumo v Rustenburg Platinum Mines (2007) 28 ILJ 2405 (CC), and any other applicable authority. Request reinstatement or, alternatively, substitution of the sanction with a lesser one. Conclude with a polite request for an appeal hearing within a reasonable time and an offer to provide further submissions.",
                    },
                  });
                  toast.success(`Generated: ${draft.title}`);
                  qc.invalidateQueries({ queryKey: ["labour-analysis", id] });
                  openDraft(draft.id);
                } catch (e: any) {
                  toast.error(e?.message ?? "Could not draft appeal");
                } finally {
                  setDrafting(false);
                }
              }}
              disabled={drafting}
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-700 bg-rose-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700/90 disabled:opacity-60"
            >
              {drafting && draftKind === "appeal_letter" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Gavel className="h-3.5 w-3.5" />
              )}
              Write dismissal appeal
            </button>
          )}
          {row?.ai_report && (
            <Link
              to={"/portal/labour-analyzer/$id/form-7-11" as any}
              params={{ id } as any}
              className="inline-flex items-center gap-1.5 rounded-md border border-gold bg-gold/10 px-3 py-1.5 text-xs font-semibold text-navy hover:bg-gold/20"
            >
              <Sparkles className="h-3.5 w-3.5" /> Complete CCMA Form 7.11
            </Link>
          )}
        </div>
      </div>

      <header className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <Scale className="h-4 w-4" /> AI Labour-Law Matter
        </div>
        <h1 className="font-display text-2xl text-navy">{row.matter_title}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {row.doc_type.replaceAll("_", " ")} · {row.original_filename} · Uploaded{" "}
          {new Date(row.created_at).toLocaleString("en-ZA")}
        </p>
        {row.student_number && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            Student №: <span className="font-mono text-navy">{row.student_number}</span>
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3 text-xs">
          {row.status === "analyzing" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 font-semibold text-amber-900">
              <Loader2 className="h-3 w-3 animate-spin" /> AI analysing…
            </span>
          )}
          {row.status === "admin_review" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-1 font-semibold text-sky-900">
              <ShieldCheck className="h-3 w-3" /> Awaiting admin review
            </span>
          )}
          {row.status === "approved" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 font-semibold text-emerald-900">
              <CheckCircle2 className="h-3 w-3" /> Approved by NDLCS admin
            </span>
          )}
          {row.status === "rejected" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 font-semibold text-rose-900">
              Rejected — see admin notes
            </span>
          )}
          {row.status === "error" && (
            <button
              type="button"
              onClick={onRetry}
              disabled={retrying}
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-1 font-semibold text-rose-900 hover:bg-rose-200"
            >
              {retrying ? "Retrying…" : "Analysis failed — retry"}
            </button>
          )}
          {typeof row.ai_confidence === "number" && (
            <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
              Confidence: <strong className="text-navy">{row.ai_confidence}%</strong>
            </span>
          )}
          {report.risk_rating && (
            <span className="rounded-full bg-muted px-2 py-1 text-muted-foreground">
              Risk: <strong className="text-navy">{report.risk_rating}</strong>
            </span>
          )}
        </div>

        {row.error_message && (
          <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
            {row.error_message}
          </p>
        )}
      </header>

      {row.admin_notes && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="mb-1 font-semibold">Admin notes</p>
          <p className="whitespace-pre-wrap">{row.admin_notes}</p>
        </div>
      )}

      {row.status === "analyzing" ? (
        <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Our AI is reading your document and applying the LRA, BCEA, EEA and Schedule 8. This
          page refreshes automatically.
        </p>
      ) : row.ai_report ? (
        <>
          {report.matter_summary && (
            <Section title="Summary">
              <p className="text-sm text-foreground/90">{report.matter_summary}</p>
            </Section>
          )}

          {Array.isArray(report.key_facts) && report.key_facts.length > 0 && (
            <Section title="Key facts">
              <ul className="ml-5 list-disc space-y-1 text-sm">
                {report.key_facts.map((f: string, i: number) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            </Section>
          )}

          {Array.isArray(report.procedural_issues) && report.procedural_issues.length > 0 && (
            <Section title="Procedural issues">
              <ul className="space-y-3">
                {report.procedural_issues.map((it: any, i: number) => (
                  <li key={i} className="rounded-md border border-border bg-muted/30 p-3">
                    <div className="mb-1 flex items-center gap-2">
                      <Severity sev={it.severity} />
                      <p className="text-sm font-semibold text-navy">{it.issue}</p>
                    </div>
                    <p className="text-xs text-foreground/80">{it.explanation}</p>
                    {it.remedy && (
                      <p className="mt-1 text-xs text-emerald-800">
                        <strong>Remedy:</strong> {it.remedy}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {Array.isArray(report.substantive_issues) && report.substantive_issues.length > 0 && (
            <Section title="Substantive issues">
              <ul className="space-y-3">
                {report.substantive_issues.map((it: any, i: number) => (
                  <li key={i} className="rounded-md border border-border bg-muted/30 p-3">
                    <div className="mb-1 flex items-center gap-2">
                      <Severity sev={it.severity} />
                      <p className="text-sm font-semibold text-navy">{it.issue}</p>
                    </div>
                    <p className="text-xs text-foreground/80">{it.explanation}</p>
                    {it.remedy && (
                      <p className="mt-1 text-xs text-emerald-800">
                        <strong>Remedy:</strong> {it.remedy}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {Array.isArray(report.applicable_law) && report.applicable_law.length > 0 && (
            <Section title="Applicable South African labour law">
              <ul className="space-y-2 text-sm">
                {report.applicable_law.map((l: any, i: number) => (
                  <li key={i}>
                    <p className="font-semibold text-navy">{l.instrument}</p>
                    <p className="text-xs text-foreground/80">{l.principle}</p>
                    {l.why_it_applies && (
                      <p className="text-xs text-muted-foreground">Why: {l.why_it_applies}</p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {report.ccma_opportunity?.applicable && (
            <Section title="CCMA opportunity">
              <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
                <p>
                  <strong>Category:</strong> {report.ccma_opportunity.category}
                </p>
                <p>
                  <strong>Form:</strong> {report.ccma_opportunity.form}
                </p>
                {report.ccma_opportunity.time_bar_days != null && (
                  <p>
                    <strong>Time bar:</strong>{" "}
                    {report.ccma_opportunity.time_bar_days} days
                  </p>
                )}
                {report.ccma_opportunity.deadline_explanation && (
                  <p className="mt-1 text-xs text-foreground/80">
                    {report.ccma_opportunity.deadline_explanation}
                  </p>
                )}
                {Array.isArray(report.ccma_opportunity.motivation_outline) && (
                  <ul className="mt-2 ml-5 list-disc text-xs">
                    {report.ccma_opportunity.motivation_outline.map((m: string, i: number) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                )}
              </div>
            </Section>
          )}

          {Array.isArray(report.next_steps) && report.next_steps.length > 0 && (
            <Section title="Recommended next steps">
              <ol className="ml-5 list-decimal space-y-1 text-sm">
                {report.next_steps.map((s: any, i: number) => (
                  <li key={i}>
                    <span className="font-medium">{s.step}</span>
                    {s.deadline_days != null && (
                      <span className="ml-2 text-xs text-rose-700">
                        (within {s.deadline_days} days)
                      </span>
                    )}
                    {s.why && (
                      <p className="text-xs text-muted-foreground">{s.why}</p>
                    )}
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {Array.isArray(report.questions_for_client) && report.questions_for_client.length > 0 && (
            <Section title="Questions we still need answered">
              <ul className="ml-5 list-disc space-y-1 text-sm">
                {report.questions_for_client.map((q: string, i: number) => (
                  <li key={i}>{q}</li>
                ))}
              </ul>
            </Section>
          )}

          {/* CCMA & support-doc generator */}
          <Section title="Draft a CCMA / hearing document">
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <div className="space-y-3">
                <div>
                  <Label>Document to draft</Label>
                  <Select value={draftKind} onValueChange={setDraftKind}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DRAFT_KINDS.map((d) => (
                        <SelectItem key={d.v} value={d.v}>
                          {d.l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Extra instructions (optional)</Label>
                  <Textarea
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    rows={3}
                    placeholder="e.g. Cite the dates in the charge sheet. Refer to Sidumo. Emphasise procedural unfairness."
                    maxLength={2000}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <Button onClick={onGenerate} disabled={drafting} className="bg-navy hover:bg-navy/90">
                  {drafting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Drafting…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" /> Generate
                    </>
                  )}
                </Button>
              </div>
            </div>

            {drafts.length > 0 && (
              <ul className="mt-4 space-y-2">
                {drafts.map((d: any) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between rounded-md border border-border bg-muted/30 p-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-navy">{d.title}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {new Date(d.created_at).toLocaleString("en-ZA")} · {d.status}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => openDraft(d.id)}
                      className="rounded-md border border-input bg-background px-3 py-1.5 text-xs text-navy hover:border-navy"
                    >
                      <FileText className="mr-1 inline h-3 w-3" /> View
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {openDraftId && (
              <div className="mt-4 space-y-2 rounded-md border border-border bg-card p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-navy">Draft preview</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        await navigator.clipboard.writeText(draftBody);
                        toast.success("Copied");
                      }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
                    >
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                  </div>
                </div>
                <pre className="max-h-[400px] overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-xs text-foreground/90">
                  {draftBody}
                </pre>
              </div>
            )}
          </Section>

          <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {report.disclaimer ||
                "This is legal support and guidance, not legal representation. Verify facts and meet CCMA / court deadlines independently."}
            </span>
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No AI report yet.</p>
      )}

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Gavel className="h-3 w-3 text-gold" /> Powered by Lovable AI · NDLCS Labour-Law Knowledge
        Base
      </p>
    </div>
  );
}
